import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { requestNameCompat, type NameCompatResult } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import { useTelegram } from '@/hooks/useTelegram';

const VERDICT_COLOR: Record<NameCompatResult['verdict'], string> = {
  resonant:    'text-emerald-300 border-emerald-400/50',
  harmonious:  'text-gold-200 border-gold-400/50',
  neutral:     'text-gold-300/80 border-gold-400/30',
  challenging: 'text-rose-300 border-rose-400/50',
};

/**
 * Two-input name compatibility — pure Pythagorean numerology, no AI.
 * Pitches a viral shareable result with a CTA back to the app.
 */
export default function NameCompat() {
  const { t } = useTranslation();
  const { tg, haptic } = useTelegram();

  const [nameA, setNameA] = useState('');
  const [nameB, setNameB] = useState('');
  const [result, setResult] = useState<NameCompatResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = nameA.trim().length > 0 && nameB.trim().length > 0;

  const compute = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await requestNameCompat(nameA.trim(), nameB.trim());
      setResult(r);
      haptic?.notificationOccurred('success');
    } catch (e) {
      setError((e as Error).message);
      haptic?.notificationOccurred('error');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
  };

  const share = () => {
    if (!result) return;
    const text =
      `${result.name_a} × ${result.name_b} = ${result.score}%\n`
      + `${result.blurb}\n\n${t('name_compat.share_cta')}`;
    if (tg?.openTelegramLink) {
      const url = 'https://t.me/share/url'
        + `?url=${encodeURIComponent(result.share_url)}`
        + `&text=${encodeURIComponent(text)}`;
      tg.openTelegramLink(url);
    } else {
      navigator.clipboard?.writeText(`${text}\n${result.share_url}`).catch(() => {});
    }
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        ✦ {t('name_compat.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('name_compat.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <OrnateFrame className="card-glow mb-4 space-y-3">
              <label className="block">
                <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('name_compat.name_a')}
                </span>
                <input
                  type="text"
                  value={nameA}
                  onChange={(e) => setNameA(e.target.value)}
                  placeholder={t('name_compat.placeholder_a')}
                  maxLength={64}
                  className="w-full rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 font-serif text-base
                             text-gold-100 outline-none transition
                             placeholder:text-gold-300/35 focus:border-gold-400"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('name_compat.name_b')}
                </span>
                <input
                  type="text"
                  value={nameB}
                  onChange={(e) => setNameB(e.target.value)}
                  placeholder={t('name_compat.placeholder_b')}
                  maxLength={64}
                  className="w-full rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 font-serif text-base
                             text-gold-100 outline-none transition
                             placeholder:text-gold-300/35 focus:border-gold-400"
                />
              </label>
            </OrnateFrame>

            <button
              type="button"
              onClick={compute}
              disabled={!canSubmit || busy}
              className="btn-primary w-full disabled:opacity-40"
            >
              {busy ? t('name_compat.calculating') : t('name_compat.calculate')}
            </button>

            {error && (
              <p className="mt-3 text-center text-xs text-rose-400">{error}</p>
            )}

            <p className="mt-4 text-center text-xs italic text-gold-300/50">
              {t('name_compat.description')}
            </p>
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="mb-4 flex items-center justify-center gap-4">
              <NameTile name={result.name_a} number={result.number_a} />
              <span className="text-2xl text-gold-400/60">×</span>
              <NameTile name={result.name_b} number={result.number_b} />
            </div>

            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 14 }}
              className={`mx-auto mb-3 flex h-28 w-28 flex-col items-center justify-center
                          rounded-full border-2 bg-night-900/60
                          ${VERDICT_COLOR[result.verdict]}`}
              style={{ filter: 'drop-shadow(0 0 16px rgba(232,195,86,0.35))' }}
            >
              <span className="font-serif text-3xl">{result.score}%</span>
              <span className="text-[10px] uppercase tracking-widest opacity-80">
                {t(`name_compat.verdict_${result.verdict}`)}
              </span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="mb-5 px-2 font-serif italic leading-relaxed text-gold-200"
            >
              {result.blurb}
            </motion.p>

            <div className="space-y-2">
              <button type="button" onClick={share} className="btn-ghost w-full">
                ✦ {t('name_compat.share')}
              </button>
              <button type="button" onClick={reset} className="btn-primary w-full">
                {t('name_compat.try_again')}
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}

function NameTile({ name, number }: { name: string; number: number }) {
  return (
    <div className="flex h-32 w-24 flex-col items-center justify-center
                    rounded-xl border border-gold-500/40
                    bg-gradient-to-br from-gold-500/15 to-night-800">
      <span className="font-serif text-3xl text-gold-300">{number}</span>
      <span className="mt-2 max-w-[88px] truncate text-[11px] text-gold-200">
        {name}
      </span>
    </div>
  );
}
